import { useEffect, useState } from 'react'
import { useModal, useModalExtra } from '../../../../_components/Modal'
import { useModifyClassList } from '../../../../common/utils/CSSUtils'
import { ChrofyShowErrors, ChrofyTableFormInput } from '../../../common/Form'
import { produce } from 'immer'
import DatePicker, { registerLocale } from 'react-datepicker'
import ja from 'date-fns/locale/ja'
import { nanoid } from 'nanoid'
import { getLastDayOfMonth } from '../../../../common/utils/DateUtils'
import { isInvalidDate } from '../extra/helpers'
import { validateForm } from '../extra/validation'

// 日本語ロケールを登録
registerLocale('ja', ja)

/**
 * DatePicker に渡す日付を安全化する。 インポートで取り込んだ不正日付（date-fns parse 失敗時の Invalid Date）が selected/minDate に渡ると react-datepicker
 * が描画時に format() で例外を投げ、画面が真っ白になるため、 有効な Date 以外は null に落とす。
 *
 * @function safeDate
 * @param {unknown} date - 日付候補
 * @returns {Date | null} 有効な Date、それ以外は null
 */
const safeDate = (date) => (date instanceof Date && !isInvalidDate(date) ? date : null)

export const UserClassificationAddCategoryModal = (props) => {
  const { defaultData = {} } = props

  // 使用中かどうかを判定
  const isInUse = defaultData?.status === '使用中'

  const [formData, setFormData] = useState({
    key: '',
    code: '',
    value: '',
    rank: undefined,
    type: '',
    memo: '',
    ...defaultData,
    // インポートで取り込んだ不正日付（Invalid Date）を formData に持ち込むと、設定押下時の
    // スキーマ検証で有効期間に英語の「Invalid input」が表示され保存できなくなる。
    // 未設定（null＝無期限）に落として開く（この行の #3231 追加対応）
    validStartDate: safeDate(defaultData?.validStartDate ?? null),
    validEndDate: safeDate(defaultData?.validEndDate ?? null)
  })

  const [errors, setErrors] = useState({
    code: [],
    value: [],
    rank: [],
    validStartDate: [],
    validEndDate: [],
    memo: []
  })

  const updateValue = (field) => (e) => {
    // 使用中の場合は値の編集を無効化
    if (field === 'value' && isInUse) {
      return
    }

    setFormData(
      produce((draft) => {
        if (field === 'rank') {
          const value = e.target ? e.target.value : e
          // 数値のみを許可（0-9のみ、整数のみ）
          const numericValue = value.toString().replace(/[^0-9]/g, '')
          draft[field] = numericValue === '' ? undefined : Number(numericValue)
        } else if (field === 'validStartDate' || field === 'validEndDate') {
          // 月選択: Dateオブジェクトまたはnull。有効開始月=月初日、有効終了月=月末日で内部保持
          if (e === null || e === undefined) {
            draft[field] = null
          } else if (e instanceof Date && !isNaN(e.getTime())) {
            if (field === 'validStartDate') {
              // 選択月の初日
              draft[field] = new Date(e.getFullYear(), e.getMonth(), 1)
            } else {
              // 選択月の末日
              draft[field] = getLastDayOfMonth(e.getFullYear(), e.getMonth() + 1)
            }
          } else {
            draft[field] = null
          }
        } else {
          draft[field] = e.target ? e.target.value : e
        }
      })
    )
  }

  useEffect(() => {
    props?.onChange(formData, setErrors)
  }, [formData, setErrors])

  return (
    <>
      <table className="form-table table-fixed w-full">
        <tbody>
          <ChrofyTableFormInput
            data-testid={'code'}
            errors={errors}
            value={formData.code}
            onChange={updateValue('code')}
            id="code"
            label="コード"
            placeholder="コードを入力してください"
            disabled={false}
            required={false}
          />
          <ChrofyTableFormInput
            data-testid={'value'}
            errors={errors}
            value={formData.value}
            onChange={updateValue('value')}
            id="value"
            label="値"
            placeholder="値を入力してください"
            disabled={isInUse}
            required
            title={isInUse ? '使用中の値は変更できません' : ''}
          />
          <ChrofyTableFormInput
            data-testid={'rank'}
            errors={errors}
            value={formData.rank ?? ''}
            onChange={updateValue('rank')}
            id="rank"
            label="ランク"
            placeholder="ランクを入力してください"
            disabled={false}
            required={false}
            type="number"
          />

          <tr>
            <th className="form-table__col-head w-[150px]">
              <label className="text-h7 font-bold">有効期間</label>
            </th>
            <td className="form-table__col-data">
              <div className="flex gap-5 items-center form-control ">
                <DatePicker
                  id="validStartDate"
                  name="validStartDate"
                  selected={safeDate(formData.validStartDate)}
                  onChange={updateValue('validStartDate')}
                  dateFormat="yyyy/MM"
                  showMonthYearPicker
                  className={`rounded-lg w-[150px] min-h-[46px] text-input text-left ${errors?.validStartDate?.length > 0 ? 'is-error' : ''}`}
                  placeholderText="YYYY/MM"
                  autoComplete="off"
                  locale="ja"
                  portal
                />
                <span>～</span>
                <DatePicker
                  id="validEndDate"
                  name="validEndDate"
                  selected={safeDate(formData.validEndDate)}
                  onChange={updateValue('validEndDate')}
                  dateFormat="yyyy/MM"
                  showMonthYearPicker
                  className={`rounded-lg w-[150px] min-h-[46px] text-input text-left ${errors?.validEndDate?.length > 0 ? 'is-error' : ''}`}
                  placeholderText="YYYY/MM"
                  autoComplete="off"
                  locale="ja"
                  minDate={safeDate(formData.validStartDate)}
                  popperPlacement="bottom-start"
                  portalId="root-portal"
                  popperContainer={({ children }) => (
                    <div style={{ position: 'absolute', top: 0, left: 0, zIndex: 1500, minWidth: 280 }}>{children}</div>
                  )}
                />
              </div>

              <ChrofyShowErrors field={'validStartDate'} errors={errors} />
              <ChrofyShowErrors field={'validEndDate'} errors={errors} />
            </td>
          </tr>
          <tr>
            <th className="form-table__col-head w-[150px]">
              <label className="text-h7 font-bold">メモ</label>
            </th>
            <td className="form-table__col-data">
              <textarea
                id="memo"
                name="memo"
                value={formData.memo || ''}
                onChange={updateValue('memo')}
                className={`rounded-lg w-full min-h-[100px] text-input text-left align-top ${errors?.memo?.length > 0 ? 'is-error' : ''}`}
                placeholder="補足情報など"
                disabled={false}
              />
              <ChrofyShowErrors field={'memo'} errors={errors} />
            </td>
          </tr>
        </tbody>
      </table>
    </>
  )
}

/** @function useOpenUserClassificationModifyCategoryModal モダルを開くためのフック */
export const useOpenUserClassificationModifyCategoryModal = ({ create, update, onModalOpen }) => {
  const showModal = useModal()
  const { _closeUpModal } = useModalExtra()

  return async (defaultData, exisitingRecords) => {
    const { promisify } = useModifyClassList('.modal-window', ['w-[700px]'])

    // モーダル表示時にデータを保存
    if (onModalOpen && defaultData) {
      onModalOpen(defaultData)
    }

    await promisify((res) => {
      const key = Math.random()

      const componentRef = {
        formDataRef: null,
        onError: null
      }

      const onChange = (formData_1, _onError) => {
        componentRef.formDataRef = formData_1
        componentRef.onError = _onError
      }

      showModal({
        title: '区分項目設定',
        isWide: false,
        onClose: res,
        body: <UserClassificationAddCategoryModal key={key} defaultData={defaultData} onChange={onChange} />,
        button: [
          {
            name: '設定',
            close: false,
            className: 'footer-btn button_base',
            click: async () => {
              const { formDataRef, onError } = componentRef

              const editMode = Boolean(defaultData)

              const hasError = validateForm(formDataRef, exisitingRecords, editMode)

              if (hasError === null) {
                // ユーザーが確定した保存は取り込み時の生値（_rawValid*）より優先される。
                // 生値を残すと有効期間を未設定で保存した際に一覧セルのフォールバック表示で
                // 取り込んだ 'abc' 等が再表示されるため破棄する（この行の #3231 追加対応）
                const savedData = {
                  ...formDataRef,
                  _rawValidStartDate: undefined,
                  _rawValidEndDate: undefined
                }

                const promise = !editMode
                  ? create({
                      ...savedData,
                      key: nanoid()
                    })
                  : update(savedData)

                await promise

                _closeUpModal()
                res()
              } else {
                onError?.(hasError)
              }
            }
          }
        ]
      })
    })
  }
}
